import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, inputCls } from './_shared';
import PhasePDFUpload from './PhasePDFUpload';
import { PHASE_DEFS } from './MapLocationPicker';

export default function AdminPhasesView({ event, onBack }) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/v1/tournaments/${event.id}/phases/admin/all`);
      setPhases(res.data);
    } catch { toast.error('Failed to load phases'); }
    finally { setLoading(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const updatePhase = (phaseKey, field, value) => {
    setPhases(prev => prev.map(p => 
      p.phase_key === phaseKey ? { ...p, [field]: value } : p
    ));
  };

  const savePhase = async (phaseKey) => {
    const phase = phases.find(p => p.phase_key === phaseKey);
    setSaving(true);
    try {
      await axiosInstance.put(`/v1/tournaments/${event.id}/phases/${phaseKey}`, {
        is_enabled: phase.is_enabled,
        start_date: phase.start_date || null,
        end_date: phase.end_date || null,
        title: phase.title || null,
        description: phase.description || null,
        rules: phase.rules || null,
        terms: phase.terms || null,
      });
      toast.success(`${PHASE_DEFS[phaseKey]?.title || phaseKey} phase updated`);
    } catch (e) {
      // e.g. the auction can't be switched once players are on teams
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save phase');
      load(); // drop the unsaved toggle so the screen matches the server
    }
    finally { setSaving(false); }
  };

  const togglePhase = (phaseKey) => {
    updatePhase(phaseKey, 'is_enabled', !phases.find(p => p.phase_key === phaseKey)?.is_enabled);
  };

  return (
    <div data-testid="admin-phases-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4">
        <i className="ph-bold ph-arrow-left" /> Back to Events
      </button>
      <h2 className="text-lg font-extrabold mb-1">{event.title}</h2>
      <p className="text-xs text-gray-400 mb-4">Tournament Phases Configuration</p>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]" /></div>
      ) : (
        <div className="space-y-3">
          {phases.map((phase) => {
            const def = PHASE_DEFS[phase.phase_key] || { title: phase.phase_key, icon: 'ph-circle', description: '' };
            const isExpanded = expandedPhase === phase.phase_key;
            
            return (
              <div key={phase.phase_key}
                className={`border rounded-xl overflow-hidden transition-all ${
                  phase.is_enabled ? 'border-[#3AAFA9] bg-[#3AAFA9]/5' : 'border-gray-200 bg-gray-50'
                }`}>
                {/* Phase Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => togglePhase(phase.phase_key)}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                        phase.is_enabled ? 'bg-[#3AAFA9] text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                      <i className={`ph-bold ${def.icon} text-lg`}></i>
                    </button>
                    <div>
                      <p className="font-bold text-sm">{def.title}</p>
                      <p className="text-xs text-gray-500">{def.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={phase.is_enabled} onChange={() => togglePhase(phase.phase_key)}
                        className="w-4 h-4 rounded text-[#3AAFA9] focus:ring-[#3AAFA9]" />
                      <span className="text-xs font-bold text-gray-500">Enabled</span>
                    </label>
                    <button onClick={() => setExpandedPhase(isExpanded ? null : phase.phase_key)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <i className={`ph-bold ${isExpanded ? 'ph-caret-up' : 'ph-caret-down'} text-gray-500`}></i>
                    </button>
                  </div>
                </div>

                {/* Expanded Config */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 space-y-4 bg-white">
                    {phase.phase_key === 'fixtures' ? (
                      /* Fixtures phase: only Release Date */
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Release Date</label>
                        <p className="text-[10px] text-gray-400 mb-1">When should the fixtures become visible to users?</p>
                        <input type="datetime-local"
                          value={(() => {
                            if (!phase.start_date) return '';
                            const d = new Date(phase.start_date);
                            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                          })()}
                          onChange={(e) => updatePhase(phase.phase_key, 'start_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                          className={inputCls} />
                      </div>
                    ) : (
                      /* All other phases: Start + End dates */
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1">Start Date</label>
                          <input type="datetime-local"
                            value={(() => {
                              if (!phase.start_date) return '';
                              const d = new Date(phase.start_date);
                              return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            })()}
                            onChange={(e) => updatePhase(phase.phase_key, 'start_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                            className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1">End Date</label>
                          <input type="datetime-local"
                            value={(() => {
                              if (!phase.end_date) return '';
                              const d = new Date(phase.end_date);
                              return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            })()}
                            onChange={(e) => updatePhase(phase.phase_key, 'end_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                            className={inputCls} />
                        </div>
                      </div>
                    )}
                    {/* Custom Title - Hide for Overview and Registration phases */}
                    {!['overview', 'register'].includes(phase.phase_key) && (
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Custom Title</label>
                        <input type="text" value={phase.title || ''} onChange={(e) => updatePhase(phase.phase_key, 'title', e.target.value)}
                          placeholder={def.title} className={inputCls} />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Description</label>
                      <textarea value={phase.description || ''} onChange={(e) => updatePhase(phase.phase_key, 'description', e.target.value)}
                        placeholder="Custom description..." rows={2} className={inputCls + ' resize-none'} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">
                        {phase.phase_key === 'overview' ? 'Event Rules PDF (Generic)' : 'Phase Rules PDF'}
                      </label>
                      <PhasePDFUpload
                        phaseKey={phase.phase_key}
                        currentUrl={phase.rules_pdf_url}
                        onUploaded={(url) => updatePhase(phase.phase_key, 'rules_pdf_url', url)}
                      />
                    </div>
                    <button onClick={() => savePhase(phase.phase_key)} disabled={saving}
                      className="w-full py-2 bg-[#2B3A67] text-white font-bold rounded-lg text-sm hover:bg-[#1e2a4a] transition-colors disabled:opacity-60">
                      {saving ? 'Saving...' : `Save ${def.title} Phase`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4 text-center">
        Enable phases to make them visible on the tournament page. Set start dates to show "Coming Soon" until the date is reached.
      </p>
    </div>
  );
}
